import { User, Calendar, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Passenger } from '@/types/pricing';

interface PassengerStepProps {
  passenger: Passenger;
  ticketingDate: string;
  onPassengerChange: (passenger: Passenger) => void;
  onTicketingDateChange: (date: string) => void;
  onBack: () => void;
  onNext: () => void;
}

export function PassengerStep({
  passenger,
  ticketingDate,
  onPassengerChange,
  onTicketingDateChange,
  onBack,
  onNext,
}: PassengerStepProps) {
  const isValid = passenger.firstName.trim() && passenger.lastName.trim() && ticketingDate.trim();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Passenger Information</h2>
        <p className="mt-1 text-sm text-slate-600">
          Enter the passenger name and ticketing date for the fare quote.
        </p>
      </div>

      {/* Passenger Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-blue-600" />
            Passenger Details
          </CardTitle>
          <CardDescription>
            The name should match the booking confirmation exactly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                placeholder="e.g., John"
                value={passenger.firstName}
                onChange={(e) =>
                  onPassengerChange({ ...passenger, firstName: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                placeholder="e.g., Smith"
                value={passenger.lastName}
                onChange={(e) =>
                  onPassengerChange({ ...passenger, lastName: e.target.value })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ticketing Date Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-blue-600" />
            Ticketing Date
          </CardTitle>
          <CardDescription>
            The date when the ticket was originally issued.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="ticketingDate">Date (DD.MM.YYYY)</Label>
            <Input
              id="ticketingDate"
              placeholder="e.g., 15.06.2024"
              value={ticketingDate}
              onChange={(e) => onTicketingDateChange(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              This date is used for historical fare pricing to determine what fares were available at the time of booking.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Button onClick={onNext} disabled={!isValid}>
          Next: Flight Segments
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
